import React, {useState, useEffect} from "react";
import CohortComp from "../Comparators/CohortComp";
import AgeComp from "../Comparators/AgeComp";
import SexComp from "../Comparators/SexComp";
import YearComp from "../Comparators/YearsComp";
import StateComp from "../Comparators/StateComp";
import InsuranceComp from "../Comparators/InsuranceComp";
import useStateType from "../Stores/StateStore";
import useCohortType from "../Stores/CohortStore";
import useAgeType from "../Stores/AgeStore";
import useSexType from "../Stores/SexStore";
import useInsuranceType from "../Stores/InsuranceStore";
import useYearType from "../Stores/YearStore";

interface DisplayComparatorMenuProps {
  displayValue: number;
  setIsComparatorOpen: () => void;
  comparator: string;
  setComparator: (val: string) => void;
  insuranceCompDisabled?: boolean;
}

const DisplayComparatorMenu: React.FC<DisplayComparatorMenuProps> = ({
  displayValue,
  setIsComparatorOpen,
  comparator,
  setComparator,
  insuranceCompDisabled = false,
}): JSX.Element => {
  const stateCompValue = useStateType((s) => s.stateCompValue);

  const [tempSelection, setTempSelection] = useState<string[]>(stateCompValue);
  const setStateCompValue = useStateType((s) => s.setStateCompValue);
  const [expandedOption, setExpandedOption] = useState<string>(comparator);

  const setCohortCompValue = useCohortType((s) => s.setCohortCompValue);
  const setAgeCompValue = useAgeType((s) => s.setAgeCompValue);
  const setSexCompValue = useSexType((s) => s.setSexCompValue);
  const setInsuranceCompValue = useInsuranceType(
    (s) => s.setInsuranceCompValue,
  );
  const setYearCompValue = useYearType((s) => s.setYearCompValue);

  function resetStates() {
    setStateCompValue([]);
    setTempSelection([]);
    setCohortCompValue([]);
    setAgeCompValue([]);
    setSexCompValue([]);
    setInsuranceCompValue([]);
    setYearCompValue([]);
  }

  const handleOptionClick = (value: string) => {
    setComparator(value);
    setExpandedOption(expandedOption === value ? "" : value);
  };

  const comparatorOptions = [
    {value: "State", label: "State"},
    {value: "Cohort", label: "Cohort"},
    {value: "Age", label: "Age"},
    {value: "Sex", label: "Sex"},
    {value: "Insurance", label: "Insurance", disabled: insuranceCompDisabled},
    {value: "Year", label: "Year"},
  ];

  const renderComparatorContent = (optionValue: string) => {
    switch (optionValue) {
      case "State":
        return (
          <StateComp
            tempSelection={tempSelection}
            setTempSelection={setTempSelection}
            displayValue={displayValue}
            onClose={setIsComparatorOpen}
          />
        );
      case "Cohort":
        return <CohortComp onClose={setIsComparatorOpen} />;
      case "Age":
        return <AgeComp onClose={setIsComparatorOpen} />;
      case "Sex":
        return <SexComp onClose={setIsComparatorOpen} />;
      case "Insurance":
        return <InsuranceComp onClose={setIsComparatorOpen} />;
      case "Year":
        return <YearComp onClose={setIsComparatorOpen} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="bg-valmainheart xl:rounded-xl flex-col items-start w-full xl:w-auto justify-start xl:max-w-min min-h-screen xl:min-h-0 xl:h-auto overflow-y-auto pb-8 xl:pb-0 xl:mb-10">
        <div className="xl:hidden mt-4 w-[93%] mx-auto flex justify-end">
          {/* <p className="text-[16px] text-[#2A424B] opacity-50">Compare By</p> */}
          {/* <button className="text-[16px] underline" onClick={() => resetStates()}>Clear All </button> */}
          <div className="flex items-center justify-between">
            <button className="ps-4" onClick={() => setIsComparatorOpen?.()}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        <div className="flex flex-col justify-between h-[calc(100vh-100px)]">
          <div className="my-4 overflow-y-auto">
            {comparatorOptions.map((option) => (
              <div key={option.value} className="">
                <button
                  className={`w-full text-left py-3 px-4 flex items-center justify-between text-xl font-medium text-valmidnight ${option.disabled ? "opacity-50" : ""}`}
                  onClick={() =>
                    !option.disabled && handleOptionClick(option.value)
                  }
                  disabled={option.disabled}>
                  <div className="flex items-center gap-x-2">
                    <div
                      className={`h-5 my-3 w-5 rounded-full p-1 border-[1px] border-[#2A424B] flex items-center justify-center`}>
                      {comparator === option.value && (
                        <div className="h-full w-full rounded-full bg-[#2A424B]"></div>
                      )}
                    </div>
                    {option.label}
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform duration-500 ${expandedOption === option.value ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {expandedOption === option.value && !option.disabled && (
                  <div className="px-4 pb-4">
                    <p className="mb-2 text-[12px] text-[#2A424B]">
                      Choose 2 options:
                    </p>
                    <div className="max-h-[200px] overflow-y-auto">
                      {renderComparatorContent(option.value)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* <div className="sticky bottom-0 left-0 right-0 bg-valmainheart flex flex-wrap justify-between w-full px-4 xl:hidden py-5">
            <button onClick={() => setIsComparatorOpen?.()}>Close</button>
            <button className="text-[18px] font-bold" onClick={() => setIsComparatorOpen?.()}>Apply Filters</button>
          </div> */}
        </div>
      </div>
    </>
  );
};

export default DisplayComparatorMenu;
